import {
  type ClipColor,
  type ClipFraming,
  colorMatrix,
  colorMatrixKey,
  coverRect,
  hasColorMatrix,
  type OverlayBox,
  placeClip,
  placeOverlay,
  type Size,
  svgColorMatrixValues,
  vignetteCss,
} from "@cva/shared";
import type { CSSProperties } from "react";

/** Strength of the blurred background behind a clip, relative to the frame width. */
export const BLUR_BACKGROUND_RATIO = 0.04;

export interface StillFrameProps {
  url: string;
  /** Pixel size of the picture. */
  source: Size;
  /** Video frame size (document px). */
  frame: Size;
  framing: ClipFraming;
  background: "color" | "blur";
  opacity?: number;
  /** Part of the frame the picture fills (split screens, cards); default the whole frame. */
  box?: OverlayBox | undefined;
  /** Corner radius of the box. */
  radius?: number | undefined;
  /** Colour correction (same matrix and vignette as the editor and the export). */
  color?: ClipColor | undefined;
}

/** SVG filter with a clip's colour matrix; `url(#id)` applies it (CSS or canvas). */
export function ColorMatrixFilter({ id, color }: { id: string; color: ClipColor }) {
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
      <filter id={id} colorInterpolationFilters="sRGB">
        <feColorMatrix type="matrix" values={svgColorMatrixValues(colorMatrix(color))} />
      </filter>
    </svg>
  );
}

/**
 * A still picture placed like a main track clip (image clips, poster frames
 * of video thumbnails). The editor's video player uses the same geometry.
 */
export function StillFrame({
  url,
  source,
  frame,
  framing,
  background,
  opacity,
  box,
  radius,
  color,
}: StillFrameProps) {
  const area = box ?? { x: 0, y: 0, width: frame.width, height: frame.height };
  const placed = box ? placeOverlay(box, source, framing) : placeClip(frame, source, framing);
  const cover = coverRect(frame, source);
  const graded = hasColorMatrix(color);
  const filterId = graded ? `grade-${colorMatrixKey(colorMatrix(color))}` : null;
  const image: CSSProperties = {
    position: "absolute",
    maxWidth: "none",
    display: "block",
    userSelect: "none",
    pointerEvents: "none",
    filter: filterId ? `url(#${filterId})` : undefined,
  };
  const vignette = vignetteCss(color?.vignette ?? 0);
  return (
    <div style={{ position: "absolute", inset: 0, opacity }}>
      {filterId && color ? <ColorMatrixFilter id={filterId} color={color} /> : null}
      {background === "blur" ? (
        <img
          src={url}
          alt=""
          draggable={false}
          decoding="sync"
          style={{
            ...image,
            left: cover.x,
            top: cover.y,
            width: cover.width,
            height: cover.height,
            filter: [
              filterId ? `url(#${filterId})` : "",
              `blur(${Math.round(frame.width * BLUR_BACKGROUND_RATIO)}px)`,
            ]
              .filter(Boolean)
              .join(" "),
            transform: "scale(1.15)",
          }}
        />
      ) : null}
      <div
        style={{
          position: "absolute",
          left: area.x,
          top: area.y,
          width: area.width,
          height: area.height,
          overflow: "hidden",
          borderRadius: radius || undefined,
        }}
      >
        <img
          src={url}
          alt=""
          draggable={false}
          decoding="sync"
          style={{
            ...image,
            left: placed.x - area.x,
            top: placed.y - area.y,
            width: placed.width,
            height: placed.height,
            transform: framing.flipX ? "scaleX(-1)" : undefined,
          }}
        />
        {vignette ? <div style={{ position: "absolute", inset: 0, background: vignette }} /> : null}
      </div>
    </div>
  );
}
